// import 'semantic-ui-css/semantic.min.css'
import 'app/styles/style.scss';
import './style.scss';

import React from 'react';
import { Component } from 'react';
import type { ReactNode, CSSProperties } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';
import { isNotLargeScreen } from 'app/utils/windowUtils';
import { Modal, Button } from 'semantic-ui-react';
import queryString from 'query-string';
import ApiCalls from '../../utils/apiCalls';
import { CHANGE_USERNAME } from 'app/redux/global/actions';
import { logout } from '../../redux/global/actions';
import FormattedServerError from '../FormattedServerError';
import NavbarFullSize from './NavbarFullSize';
import NavbarBurger from './NavbarBurger';
import IntlCrawlableLink from '../IntlCrawlableLink';
import { UNAUTHORIZED } from 'app/utils/APICodes';
import isAcquisitionSoftware from 'app/utils/isAcquisitionSoftware';
import { selectAiOnlyConfiguration } from 'app/redux/aiOnlyConfiguration/reducer';
import { selectPermissionAcquisition, selectPermissions } from 'app/redux/permissions/reducer';
import type { PermissionsState } from 'app/redux/permissions/reducer';
import { selectLoggedIn, selectSubscribed } from 'app/redux/global/selectors';
import { selectRoute } from 'app/redux/route/reducer';
import { selectLocale } from 'app/redux/language/selectors';

// @ts-ignore
const { electron } = (window as any).nodeModules ?? {};

const FULL_SCREEN_EVENTS = [
  'enter-full-screen',
  'leave-full-screen',
  'enter-html-full-screen',
  'leave-html-full-screen',
  'maximize',
  'unmaximize',
  'minimize',
];

interface NavigationItem {
  routes: string[];
  messageId: string;
  titleMessageId?: string;
  icon?: string;
  menuName?: string;
  subMenu?: NavigationItem[];
  isLoggedInNeeded?: boolean;
  hasNotSubscribedNeeded?: boolean;
}

interface ErrorMessage {
  response?: {
    status?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface HeaderProps {
  dispatch: (action: unknown) => void;
  isLoggedIn: boolean;
  style?: CSSProperties;
  hasAcquisitionPermissions: boolean;
  hasSubscribed: boolean;
  permissions: PermissionsState;
  aiOnlyConfiguration: { enabled: boolean };
  intl: any;
  locale?: string;
}

interface HeaderState {
  errorMessage: ErrorMessage | null;
}

class Header extends Component<HeaderProps, HeaderState> {
  electronItems: NavigationItem[] = [
    {
      routes: ['/acquisition'],
      messageId: 'header.acquisition',
      titleMessageId: 'header.acquisition.title',
    },
    {
      routes: ['/viewer'],
      messageId: 'header.interpretation',
      titleMessageId: 'header.interpretation.title',
    },
    {
      routes: ['/monitoring'],
      messageId: 'header.history',
      titleMessageId: 'header.history.title',
    },
    ...(isAcquisitionSoftware()
      ? []
      : [
          {
            routes: ['/pacs-query'],
            messageId: 'header.pacs',
            titleMessageId: 'header.pacs.title',
          },
        ]),
  ];

  websiteItems: NavigationItem[] = [
    {
      routes: ['/'],
      messageId: 'header.home',
      icon: 'home',
      titleMessageId: 'header.home',
    },
    {
      routes: ['/acquisition'],
      messageId: 'header.acquisition',
      titleMessageId: 'header.acquisition.title',
    },
    {
      routes: ['/viewer'],
      messageId: 'header.interpretation',
      titleMessageId: 'header.interpretation.title',
    },
    {
      routes: ['/monitoring'],
      messageId: 'header.history',
      titleMessageId: 'header.history.title',
      isLoggedInNeeded: true,
    },
    {
      routes: ['/management'],
      messageId: 'header.management',
      titleMessageId: 'header.management.title',
      isLoggedInNeeded: true,
    },
    {
      routes: ['/admin'],
      messageId: 'header.admin',
      titleMessageId: 'header.admin.title',
      isLoggedInNeeded: true,
    },
  ];

  websiteMiddleItems: NavigationItem[] = [
    {
      routes: [],
      messageId: 'header.solutions',
      menuName: 'header.solutions',
      subMenu: [
        {
          routes: ['/picoxia-acquisition'],
          messageId: 'header.solutions.picoxia-acquisition',
          titleMessageId: 'header.solutions.picoxia-acquisition.title',
        },
        {
          routes: ['/picoxia-intelligence'],
          messageId: 'header.solutions.picoxia-intelligence',
          titleMessageId: 'header.solutions.picoxia-intelligence.title',
        },
        {
          routes: ['/picoxia-teleradiology'],
          messageId: 'header.solutions.picoxia-teleradiology',
          titleMessageId: 'header.solutions.picoxia-teleradiology.title',
        },
      ],
    },
    {
      routes: ['/purchase'],
      messageId: 'header.purchase',
      titleMessageId: 'header.purchase.title',
      hasNotSubscribedNeeded: true,
    },
    {
      routes: [],
      messageId: 'header.resources',
      menuName: 'header.resources',
      subMenu: [
        {
          routes: ['/tutorials'],
          messageId: 'header.resources.tutorials',
          titleMessageId: 'header.resources.tutorials.title',
        },
        {
          routes: ['/#scientific-validation'],
          messageId: 'header.resources.scientific-validation',
          titleMessageId: 'header.resources.scientific-validation.title',
        },
        {
          routes: ['/cheat-sheet'],
          messageId: 'header.resources.cheat-sheets',
          titleMessageId: 'header.resources.cheat-sheets.title',
        },
      ],
    },
    {
      routes: ['/download'],
      messageId: 'header.download',
      titleMessageId: 'header.download.title',
    },
  ];

  constructor(props: HeaderProps) {
    super(props);
    this.state = {
      errorMessage: null,
    };
  }

  componentDidMount = () => {
    this.listenOnResize();
    if (this.props.isLoggedIn) {
      this.getUserName();
    }
  };

  componentWillUnmount = () => {
    if (electron) {
      const win = electron.remote.getCurrentWindow();
      FULL_SCREEN_EVENTS.forEach((eventName) => win.removeListener(eventName, this.onResize));
    }
  };

  getWebSiteItems = () => {
    const { hasAcquisitionPermissions, permissions } = this.props;
    let websiteItems = _.cloneDeep(this.websiteItems);

    if (!hasAcquisitionPermissions && !isAcquisitionSoftware()) {
      websiteItems = _.reject(websiteItems, { messageId: 'header.acquisition' });
    }
    const isTeamMember = !_.isEmpty(permissions.teams);
    const isManager = permissions.managerPermissions.manager_name;
    const isAdmin = permissions.admin;
    if (!isTeamMember && !isAdmin) {
      websiteItems = _.reject(websiteItems, { messageId: 'header.admin' });
    }
    if (!isTeamMember && !isAdmin && !isManager) {
      websiteItems = _.reject(websiteItems, { messageId: 'header.management' });
    }

    return websiteItems;
  };

  // eslint-disable-next-line react/destructuring-assignment
  formatMessage = (...args: any[]) => this.props.intl.formatMessage(...args);

  listenOnResize = () => {
    if (!electron) return;
    const win = electron.remote.getCurrentWindow();
    // We forceUpdate to force refresh of maximize icon.
    FULL_SCREEN_EVENTS.forEach((eventName) => win.on(eventName, this.onResize));
  };

  onResize = () => this.forceUpdate();

  switchFullScreenMode = () => {
    if (!electron) return;
    const win = electron.remote.getCurrentWindow();
    win.setFullScreen(!win.isFullScreen());
    this.forceUpdate();
  };

  getUserName = () => {
    ApiCalls.getMail()
      .then((response: any) => {
        this.props.dispatch({ type: CHANGE_USERNAME, username: response.data.mail });
        // this.setState({ username: response.data.mail });
      })
      .catch((error: any) => {
        this.setState({ errorMessage: error });
      });
  };

  getUserNameOnLinkClick = () => {
    if (this.props.isLoggedIn) {
      this.getUserName();
    }
  };

  redirectToLogin = () => {
    this.setState({ errorMessage: null });
    this.props.dispatch(logout({ redirectRoute: '/login' }));
  };

  renderDisconnectedModal = () => {
    const { errorMessage } = this.state;
    const statusCode = errorMessage?.response?.status;
    const isUserDisconnected = statusCode === UNAUTHORIZED;

    if (errorMessage) {
      console.log(errorMessage);
    }

    if (isUserDisconnected) {
      return (
        <Modal onClose={this.redirectToLogin} open={isUserDisconnected}>
          <Modal.Header>
            <FormattedMessage id="header.disconnectedModal.header" />
          </Modal.Header>
          <Modal.Content>
            <FormattedServerError error={errorMessage} />
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={this.redirectToLogin}
              content={this.formatMessage({ id: 'header.disconnectedModal.ok' })}
              positive
            />
          </Modal.Actions>
        </Modal>
      );
    }
  };

  linkIsActive = (linkPathName: string) => {
    const { locale } = this.props;
    if (window.location.pathname.replace('C:/', '') === `/${locale}/viewer`) {
      const queryStringParsed = queryString.parse(window.location.search, {
        arrayFormat: 'bracket',
      });
      if ((queryStringParsed as any).mode === 'acquisition') {
        const isAcquisitionCheck = linkPathName === `/${locale}/acquisition`;
        if (isAcquisitionCheck) return 'active ';
        return '';
      }
    }
    const pathname = window.location.pathname.replace('C:/', '');
    return `/${locale}${linkPathName}` === pathname ? 'active ' : '';
  };

  getNavigationItemClassName = (linkPathNames: string, classNameBase: string) =>
    `${this.linkIsActive(linkPathNames)}${classNameBase}`;

  renderNavigationLink = (
    linkPathNames: string[],
    content: ReactNode,
    titleId: string,
    classNameBase: string,
    onClickEvent?: () => void
  ) => {
    const title = titleId ? this.formatMessage({ id: titleId }) : '';
    return (
      <li key={String(content)}>
        <IntlCrawlableLink
          url={linkPathNames[0]}
          key={linkPathNames[0]}
          className={this.getNavigationItemClassName(linkPathNames[0], classNameBase)}
          onClick={() => {
            if (onClickEvent !== undefined) {
              onClickEvent();
            }
            this.getUserNameOnLinkClick();
          }}
        >
          {content}
        </IntlCrawlableLink>
      </li>
    );
  };

  render() {
    const isASmallScreenAndWebsite = isNotLargeScreen() && process.env.PLATFORM === 'website';

    const { style, hasAcquisitionPermissions, isLoggedIn, hasSubscribed, aiOnlyConfiguration } =
      this.props;
    let leftItems =
      process.env.PLATFORM !== 'website' ? this.electronItems : this.getWebSiteItems();
    const middleItems = process.env.PLATFORM !== 'website' ? [] : this.websiteMiddleItems;
    if (!hasAcquisitionPermissions && !isAcquisitionSoftware()) {
      leftItems = _.reject(leftItems, { messageId: 'header.acquisition' });
    }

    /** AI ONLY */
    /** @type {boolean} */
    const aiOnlyEnabled = aiOnlyConfiguration.enabled;
    if (aiOnlyEnabled) {
      leftItems = _.reject(leftItems, { messageId: 'header.history' });
    }

    return (
      <header>
        {isASmallScreenAndWebsite ? (
          <NavbarBurger
            leftItems={leftItems}
            middleItems={middleItems}
            isLoggedIn={isLoggedIn}
            hasSubscribed={hasSubscribed}
            renderNavigationLink={this.renderNavigationLink}
          />
        ) : (
          <NavbarFullSize
            leftItems={leftItems}
            middleItems={middleItems}
            isLoggedIn={isLoggedIn}
            hasSubscribed={hasSubscribed}
            renderNavigationLink={this.renderNavigationLink}
            switchFullScreenMode={this.switchFullScreenMode}
            style={style}
          />
        )}
        {this.renderDisconnectedModal()}
      </header>
    );
  }
}

const mapStateToProps = (state: any) => ({
  route: selectRoute(state),
  locale: selectLocale(state),
  isLoggedIn: selectLoggedIn(state),
  hasSubscribed: selectSubscribed(state),
  hasAcquisitionPermissions: selectPermissionAcquisition(state),
  permissions: selectPermissions(state),
  aiOnlyConfiguration: selectAiOnlyConfiguration(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Header));
